import {
	hasAdminAccess,
	logout,
	useAuth,
	WhatsAppTemplateCatalog,
} from "@repo/ui";
import { Loader2 } from "lucide-react";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { AdminTasksPage } from "./components/AdminTasksPage";
import { AgentDetailsPage } from "./components/AgentDetailsPage";
import { AgentsPage } from "./components/AgentsPage";
import { AlertsPage } from "./components/AlertsPage";
import { CallerDetailsPage } from "./components/CallerDetailsPage";
import { CallersPage } from "./components/CallersPage";
import { CallsPage } from "./components/CallsPage";
import { CompaniesPage } from "./components/CompaniesPage";
import { CompanyDetailsPage } from "./components/CompanyDetailsPage";
import { Dashboard } from "./components/Dashboard";
import { DeadLetterNotice } from "./components/DeadLetterNotice";
import { LoginPage } from "./components/LoginPage";
import { Navbar } from "./components/Navbar";
import { PhoneNumbersPage } from "./components/PhoneNumbersPage";
import { PiiReviewPage } from "./components/PiiReviewPage";
import { ServiceDetailsPage } from "./components/ServiceDetailsPage";
import { ServicesPage } from "./components/ServicesPage";
import { SettingsPage } from "./components/SettingsPage";
import { Sidebar } from "./components/Sidebar";
import { TestRoutingPage } from "./components/TestRoutingPage";
import { UserDetailsPage } from "./components/UserDetailsPage";
import { UsersPage } from "./components/UsersPage";
import { Toaster } from "./components/ui/sonner";
import { WhatsappSendersPage } from "./components/WhatsappSendersPage";
import { DataProvider } from "./contexts/DataContext";

const AssistedOnboardingPage = lazy(() =>
	import("./components/AssistedOnboardingPage").then((module) => ({
		default: module.AssistedOnboardingPage,
	})),
);
const AuditFindingsPage = lazy(() =>
	import("./components/AuditFindingsPage").then((module) => ({
		default: module.AuditFindingsPage,
	})),
);
const AuditLogsPage = lazy(() =>
	import("./components/AuditLogsPage").then((module) => ({
		default: module.AuditLogsPage,
	})),
);
const CogsLedgerPage = lazy(() =>
	import("./components/CogsLedgerPage").then((module) => ({
		default: module.CogsLedgerPage,
	})),
);
const DeadLettersPage = lazy(() =>
	import("./components/DeadLettersPage").then((module) => ({
		default: module.DeadLettersPage,
	})),
);
const DeveloperTestsPage = lazy(() =>
	import("./components/DeveloperTestsPage").then((module) => ({
		default: module.DeveloperTestsPage,
	})),
);
const EventsPage = lazy(() =>
	import("./components/EventsPage").then((module) => ({
		default: module.EventsPage,
	})),
);
const LogsPage = lazy(() =>
	import("./components/LogsPage").then((module) => ({
		default: module.LogsPage,
	})),
);
const PlaybookRunsPage = lazy(() =>
	import("./components/PlaybookRunsPage").then((module) => ({
		default: module.PlaybookRunsPage,
	})),
);
const TcdrsPage = lazy(() =>
	import("./components/TcdrsPage").then((module) => ({
		default: module.TcdrsPage,
	})),
);

type AdminRoute = {
	candidateId?: string;
	page: string;
	companyId?: string;
	callerId?: string;
	itemId?: string;
};

const pagePaths: Record<string, string> = {
	dashboard: "/",
	alerts: "/alerts",
	"dead-letters": "/dead-letters",
	"admin-tasks": "/admin-tasks",
	"assisted-onboarding": "/assisted-onboarding",
	"pii-review": "/pii-review",
	calls: "/calls",
	cogs: "/cogs",
	callers: "/callers",
	companies: "/companies",
	services: "/services",
	users: "/users",
	agents: "/agents",
	"playbook-runs": "/playbook-runs",
	"phone-numbers": "/phone-numbers",
	"whatsapp-senders": "/whatsapp-senders",
	"whatsapp-templates": "/whatsapp-templates",
	"test-routing": "/test-routing",
	events: "/raw-events",
	tcdrs: "/t-crds",
	"audit-logs": "/audit-logs",
	"audit-findings": "/audit-findings",
	logs: "/logs",
	"developer-tests": "/developer-tests",
	settings: "/settings",
};

const compactSidebarQuery = "(min-width: 921px) and (max-width: 1200px)";

function parseRoute(pathname: string): AdminRoute {
	const segments = pathname.split("/").filter(Boolean).map(decodeURIComponent);
	const [section, first, second] = segments;

	switch (section) {
		case undefined:
			return { page: "dashboard" };
		case "calls":
			return { page: "calls", itemId: first };
		case "pii-review":
			return {
				page: segments[3] === "edit" ? "pii-review-editor" : "pii-review",
				candidateId: segments[3] === "edit" ? segments[4] : undefined,
				companyId: first,
				itemId: second,
			};
		case "callers":
			return first && second
				? { page: "caller-details", companyId: first, callerId: second }
				: { page: "callers" };
		case "outbound-playbooks":
			return { page: "agents" };
		case "companies":
			return first
				? { page: "company-details", companyId: first }
				: { page: "companies" };
		case "raw-events":
			return { page: "events", itemId: first };
		case "t-crds":
			return { page: "tcdrs", itemId: first };
		case "audit-logs":
			return { page: "audit-logs", itemId: first };
		default: {
			const page = Object.entries(pagePaths).find(
				([, path]) => path === `/${section}`,
			)?.[0];
			return { page: page ?? "dashboard" };
		}
	}
}

function routeForPage(page: string) {
	return pagePaths[page] ?? "/";
}

export default function App() {
	const { user, loading } = useAuth();
	const [authorizedUid, setAuthorizedUid] = useState<string | null>(null);
	const [authorizationError, setAuthorizationError] = useState("");

	useEffect(() => {
		if (loading) return;

		if (!user) {
			setAuthorizedUid(null);
			return;
		}

		let active = true;
		setAuthorizedUid(null);

		setAuthorizationError("");

		user
			.getIdTokenResult(true)
			.then((result) => {
				if (!active) return;
				if (hasAdminAccess(result.claims)) {
					setAuthorizedUid(user.uid);
				} else {
					logout().catch(console.error);
					setAuthorizationError(
						"Access denied: systemAdmin privileges required.",
					);
				}
			})
			.catch((error) => {
				if (!active) return;
				console.error("Error checking admin privileges:", error);
				logout().catch(console.error);
				setAuthorizationError(
					"Could not verify systemAdmin privileges. Please try again.",
				);
			});
		return () => {
			active = false;
		};
	}, [user, loading]);

	const initialRoute = useMemo(() => parseRoute(window.location.pathname), []);
	const [selectedPiiCandidateId, setSelectedPiiCandidateId] = useState<
		string | undefined
	>(initialRoute.candidateId);
	const [currentPage, setCurrentPageState] = useState(initialRoute.page);
	const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(
		["company-details", "pii-review", "pii-review-editor"].includes(
			initialRoute.page,
		)
			? (initialRoute.companyId ?? null)
			: null,
	);
	const [openCompanyEditor, setOpenCompanyEditor] = useState(false);
	const [createForCompanyId, setCreateForCompanyId] = useState<string | null>(
		null,
	);
	const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
	const [selectedServiceId, setSelectedServiceId] = useState<string | null>(
		null,
	);
	const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
	const [selectedCallerId, setSelectedCallerId] = useState<string | null>(
		initialRoute.callerId ?? null,
	);
	const [selectedCallerCompanyId, setSelectedCallerCompanyId] = useState<
		string | null
	>(initialRoute.companyId ?? null);
	const [selectedDeepLinkId, setSelectedDeepLinkId] = useState<string | null>(
		initialRoute.itemId ?? null,
	);
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
		() => window.matchMedia?.(compactSidebarQuery)?.matches ?? false,
	);
	const [isMobileViewport, setIsMobileViewport] = useState(
		() => window.matchMedia?.("(max-width: 920px)")?.matches ?? false,
	);
	const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

	useEffect(() => {
		const mediaQuery = window.matchMedia?.(compactSidebarQuery);
		if (!mediaQuery) return;
		const syncSidebar = () => setIsSidebarCollapsed(mediaQuery.matches);
		mediaQuery.addEventListener("change", syncSidebar);
		return () => mediaQuery.removeEventListener("change", syncSidebar);
	}, []);

	useEffect(() => {
		const mediaQuery = window.matchMedia?.("(max-width: 920px)");
		if (!mediaQuery) return;
		const syncMobileViewport = () => {
			setIsMobileViewport(mediaQuery.matches);
			if (!mediaQuery.matches) setIsMobileSidebarOpen(false);
		};
		mediaQuery.addEventListener("change", syncMobileViewport);
		return () => mediaQuery.removeEventListener("change", syncMobileViewport);
	}, []);

	const setCurrentPage = (page: string) => {
		setSelectedDeepLinkId(null);
		setOpenCompanyEditor(false);
		setCreateForCompanyId(null);
		setCurrentPageState(page);
		window.history.pushState(null, "", routeForPage(page));
	};
	const setSelectedCallSid = (callSid: string | null) => {
		setSelectedDeepLinkId(callSid);
		window.history.pushState(
			null,
			"",
			callSid ? `/calls/${encodeURIComponent(callSid)}` : "/calls",
		);
	};

	useEffect(() => {
		const handlePopState = () => {
			const route = parseRoute(window.location.pathname);
			setOpenCompanyEditor(false);
			setCreateForCompanyId(null);
			setCurrentPageState(route.page);
			setSelectedPiiCandidateId(route.candidateId);
			setSelectedCompanyId(
				["company-details", "pii-review", "pii-review-editor"].includes(
					route.page,
				)
					? (route.companyId ?? null)
					: null,
			);
			setSelectedCallerCompanyId(
				route.page === "caller-details" ? (route.companyId ?? null) : null,
			);
			setSelectedCallerId(
				route.page === "caller-details" ? (route.callerId ?? null) : null,
			);
			setSelectedDeepLinkId(route.itemId ?? null);
		};

		window.addEventListener("popstate", handlePopState);
		return () => window.removeEventListener("popstate", handlePopState);
	}, []);

	const handleLogout = () => {
		logout();
		setCurrentPage("dashboard");
		setSelectedCompanyId(null);
		setSelectedUserId(null);
		setSelectedServiceId(null);
		setSelectedAgentId(null);
		setSelectedCallerId(null);
		setSelectedCallerCompanyId(null);
		setAuthorizedUid(null);
		setAuthorizationError("");
	};

	const handleNavigateToCompany = (companyId: string, edit = false) => {
		setSelectedCompanyId(companyId);
		setOpenCompanyEditor(edit);
		setCurrentPageState("company-details");
		window.history.pushState(
			null,
			"",
			`/companies/${encodeURIComponent(companyId)}`,
		);
	};
	const handleNavigateToCall = (callSid: string) => {
		setSelectedDeepLinkId(callSid);
		setCurrentPageState("calls");
		window.history.pushState(null, "", `/calls/${encodeURIComponent(callSid)}`);
	};
	const handleOpenPiiCase = (
		companyId: string,
		callSid: string,
		edit = false,
		candidateId?: string,
	) => {
		setSelectedCompanyId(companyId);
		setSelectedDeepLinkId(callSid);
		setCurrentPageState(edit ? "pii-review-editor" : "pii-review");
		setSelectedPiiCandidateId(candidateId);
		window.history.pushState(
			null,
			"",
			`/pii-review/${encodeURIComponent(companyId)}/${encodeURIComponent(callSid)}${edit ? `/edit${candidateId ? `/${encodeURIComponent(candidateId)}` : ""}` : ""}`,
		);
	};

	const handleNavigateToOnboarding = (onboardingId: string) => {
		setCurrentPageState("assisted-onboarding");
		window.history.pushState(
			null,
			"",
			`/assisted-onboarding?onboardingId=${encodeURIComponent(onboardingId)}`,
		);
	};

	const handleNavigateToUser = (userId: string) => {
		setSelectedUserId(userId);
		setCurrentPageState("user-details");
	};

	const handleNavigateToService = (serviceId: string) => {
		setSelectedServiceId(serviceId);
		setCurrentPageState("service-details");
	};

	const handleCreateForCompany = (
		page: "services" | "users",
		companyId: string,
	) => {
		setCreateForCompanyId(companyId);
		setCurrentPageState(page);
		window.history.pushState(null, "", routeForPage(page));
	};

	const handleNavigateToAgent = (agentId: string) => {
		setSelectedAgentId(agentId);
		setCurrentPageState("agent-details");
	};

	const handleNavigateToCaller = (companyId: string, callerId: string) => {
		setSelectedCallerCompanyId(companyId);
		setSelectedCallerId(callerId);
		setCurrentPageState("caller-details");
		window.history.pushState(
			null,
			"",
			`/callers/${encodeURIComponent(companyId)}/${encodeURIComponent(callerId)}`,
		);
	};

	const handleNavigateBack = (targetPage: string) => {
		setCurrentPage(targetPage);
		setSelectedCompanyId(null);
		setSelectedUserId(null);
		setSelectedServiceId(null);
		setSelectedAgentId(null);
		setSelectedCallerId(null);
		setSelectedCallerCompanyId(null);
	};

	if (loading) {
		return (
			<div className="h-screen w-screen flex items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin" />
			</div>
		);
	}

	if (user && user.uid !== authorizedUid) {
		return (
			<div className="h-screen w-screen flex items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin" />
			</div>
		);
	}

	if (!user) {
		return (
			<>
				<LoginPage
					initialError={authorizationError}
					onError={setAuthorizationError}
				/>
				<Toaster />
			</>
		);
	}

	const renderPage = () => {
		switch (currentPage) {
			case "dashboard":
				return <Dashboard onNavigate={setCurrentPage} />;
			case "events":
				return <EventsPage selectedEventId={selectedDeepLinkId} />;
			case "tcdrs":
				return <TcdrsPage selectedCallCid={selectedDeepLinkId} />;
			case "companies":
				return <CompaniesPage onNavigateToCompany={handleNavigateToCompany} />;
			case "company-details":
				return selectedCompanyId ? (
					<CompanyDetailsPage
						companyId={selectedCompanyId}
						initiallyEditing={openCompanyEditor}
						onNavigateBack={() => handleNavigateBack("companies")}
						onNavigateToService={handleNavigateToService}
						onAddService={() =>
							handleCreateForCompany("services", selectedCompanyId)
						}
						onAddUser={() => handleCreateForCompany("users", selectedCompanyId)}
						onNavigateToOnboarding={handleNavigateToOnboarding}
					/>
				) : (
					<CompaniesPage onNavigateToCompany={handleNavigateToCompany} />
				);
			case "users":
				return (
					<UsersPage
						onNavigateToUser={handleNavigateToUser}
						createForCompanyId={createForCompanyId}
						onCreateDialogClose={() => setCreateForCompanyId(null)}
					/>
				);
			case "user-details":
				return selectedUserId ? (
					<UserDetailsPage
						userId={selectedUserId}
						onNavigateBack={() => handleNavigateBack("users")}
					/>
				) : (
					<UsersPage onNavigateToUser={handleNavigateToUser} />
				);
			case "services":
				return (
					<ServicesPage
						onNavigateToService={handleNavigateToService}
						createForCompanyId={createForCompanyId}
						onCreateDialogClose={() => setCreateForCompanyId(null)}
					/>
				);
			case "service-details":
				return selectedServiceId ? (
					<ServiceDetailsPage
						serviceId={selectedServiceId}
						onNavigateBack={() => handleNavigateBack("services")}
					/>
				) : (
					<ServicesPage onNavigateToService={handleNavigateToService} />
				);
			case "phone-numbers":
				return <PhoneNumbersPage />;
			case "whatsapp-templates":
				return <WhatsAppTemplateCatalog canManage />;
			case "whatsapp-senders":
				return <WhatsappSendersPage />;
			case "test-routing":
				return <TestRoutingPage />;
			case "agents":
				return <AgentsPage onNavigateToAgent={handleNavigateToAgent} />;
			case "playbook-runs":
				return <PlaybookRunsPage />;
			case "agent-details":
				return selectedAgentId ? (
					<AgentDetailsPage
						agentId={selectedAgentId}
						onNavigateBack={() => handleNavigateBack("agents")}
					/>
				) : (
					<AgentsPage onNavigateToAgent={handleNavigateToAgent} />
				);
			case "settings":
				return <SettingsPage />;
			case "developer-tests":
				return <DeveloperTestsPage />;
			case "calls":
				return (
					<CallsPage
						selectedCallSid={selectedDeepLinkId}
						onSelectedCallSidChange={setSelectedCallSid}
						onOpenPiiCase={(companyId, callSid) =>
							handleOpenPiiCase(companyId, callSid)
						}
					/>
				);
			case "pii-review":
			case "pii-review-editor":
				return (
					<PiiReviewPage
						companyId={selectedCompanyId}
						callSid={selectedDeepLinkId}
						editing={currentPage === "pii-review-editor"}
						candidateId={selectedPiiCandidateId}
						onOpenCase={(companyId, callSid) =>
							handleOpenPiiCase(companyId, callSid)
						}
						onOpenEditor={(companyId, callSid, candidateId) =>
							handleOpenPiiCase(companyId, callSid, true, candidateId)
						}
						onBackToQueue={() => setCurrentPage("pii-review")}
					/>
				);
			case "cogs":
				return (
					<CogsLedgerPage
						onNavigateToCompany={handleNavigateToCompany}
						onNavigateToCall={handleNavigateToCall}
					/>
				);
			case "callers":
				return <CallersPage onNavigateToCaller={handleNavigateToCaller} />;
			case "caller-details":
				return selectedCallerId && selectedCallerCompanyId ? (
					<CallerDetailsPage
						companyId={selectedCallerCompanyId}
						callerId={selectedCallerId}
						onBack={() => handleNavigateBack("callers")}
					/>
				) : (
					<CallersPage onNavigateToCaller={handleNavigateToCaller} />
				);
			case "logs":
				return <LogsPage />;
			case "audit-findings":
				return <AuditFindingsPage />;
			case "audit-logs":
				return <AuditLogsPage selectedLogId={selectedDeepLinkId} />;
			case "alerts":
				return <AlertsPage />;
			case "dead-letters":
				return <DeadLettersPage />;
			case "admin-tasks":
				return <AdminTasksPage />;
			case "assisted-onboarding":
				return <AssistedOnboardingPage />;
			default:
				return <EventsPage selectedEventId={selectedDeepLinkId} />;
		}
	};

	return (
		<DataProvider currentPage={currentPage}>
			<div className="admin-app-shell">
				<Navbar
					onLogout={handleLogout}
					isSidebarCollapsed={isSidebarCollapsed}
					isMobileSidebarOpen={isMobileSidebarOpen}
					onToggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
					onOpenMobileSidebar={() => setIsMobileSidebarOpen(true)}
					userEmail={user.email || ""}
					currentPage={currentPage}
				/>
				<div className="admin-app-body">
					<Sidebar
						currentPage={currentPage}
						onNavigate={setCurrentPage}
						isCollapsed={isSidebarCollapsed}
						isMobileOpen={isMobileSidebarOpen}
						isMobileViewport={isMobileViewport}
						onMobileClose={() => setIsMobileSidebarOpen(false)}
					/>
					<main className="admin-main" id="main-content">
						{currentPage !== "dead-letters" && (
							<DeadLetterNotice onNavigate={setCurrentPage} />
						)}
						<div
							className={`admin-page-content${currentPage === "dead-letters" ? " admin-page-content-wide" : ""}`}
						>
							<Suspense
								fallback={
									<div
										className="flex min-h-40 items-center justify-center gap-3 text-sm text-muted-foreground"
										role="status"
									>
										<Loader2
											className="size-5 animate-spin"
											aria-hidden="true"
										/>
										Loading section…
									</div>
								}
							>
								{renderPage()}
							</Suspense>
						</div>
					</main>
				</div>
			</div>
			<Toaster />
		</DataProvider>
	);
}
